import React from 'react';
import { useTranslation } from 'localization';
import type { FactorRosterMember } from '../../../../types/statusFactors';

export interface ContributionLineProps {
  member: FactorRosterMember;
  /** Called with the chosen factor id, or `null` for the member's overall status. */
  onPick?: (sphereId: string, factorId: string | null) => void;
  /** Called when a broken contribution is reset to the member's overall status. */
  onUseOverall?: (sphereId: string) => void;
}

const BROKEN_FALLBACK: Record<string, string> = {
  factor_disabled: 'Factor is turned off',
  factor_missing: 'Factor no longer exists',
};

/**
 * MEMBER's second row: what the member contributes to this rollup. Rendering only — which of the
 * three shapes shows comes from the server's `contributionChoice`, `contributionOptions` and
 * `contributionUnavailable`; a hidden member shows nothing.
 */
const ContributionChoice: React.FC<ContributionLineProps> = ({ member, onPick, onUseOverall }) => {
  const { t } = useTranslation();

  if (member.contributionUnavailable) {
    const reason = member.contributionUnavailable.reason;
    return (
      <span className="rollup-roster__contribution rollup-roster__contribution--broken" data-testid="roster-contribution-broken">
        {t(`statusFactors.roster.brokenReason.${reason}`, BROKEN_FALLBACK[reason] ?? reason)}.{' '}
        <button type="button" className="rollup-roster__contribution-link" onClick={() => onUseOverall?.(member.sphereId)}>
          {t('statusFactors.roster.useOverall', 'Use overall')}
        </button>
      </span>
    );
  }

  if (member.contributionChoice === 'pickable') {
    const label = t('statusFactors.roster.contributesTo', 'Contributes to this rollup');
    const value = member.contribution.kind === 'factor' ? (member.contribution.factorId ?? '') : '';
    return (
      <select
        className="rollup-roster__contribution-select"
        data-testid="roster-contribution-select"
        aria-label={label}
        title={label}
        value={value}
        onChange={(event) => onPick?.(member.sphereId, event.target.value || null)}
      >
        <option value="">{t('statusFactors.roster.overallStatus', 'Overall status')}</option>
        {(member.contributionOptions ?? []).map((option) => (
          <option key={option.factorId} value={option.factorId}>
            {option.factorName}
          </option>
        ))}
      </select>
    );
  }

  if (member.contributionChoice === 'fixed') {
    return (
      <span className="rollup-roster__contribution rollup-roster__contribution--fixed" data-testid="roster-contribution-fixed">
        {t('statusFactors.roster.ruleSupplied', 'overall status · rule-supplied')}
      </span>
    );
  }

  return null;
};

/** A weight of 0 is still counted — it simply moves nothing; the row keeps its normal treatment. */
const ContributionLine: React.FC<ContributionLineProps> = (props) => {
  const { t } = useTranslation();
  const { member } = props;
  if (member.hidden) return null;
  const countedForNothing = member.influence === 0 && member.weightMode !== 'na' && !member.contributionUnavailable;
  return (
    <>
      <ContributionChoice {...props} />
      {countedForNothing && (
        <span className="rollup-roster__contribution rollup-roster__contribution--zero" data-testid="roster-contribution-zero">
          {t('statusFactors.roster.weightEdit.contributesNothing', 'counted · contributes nothing')}
        </span>
      )}
    </>
  );
};

export default ContributionLine;
